import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import {
  CheckoutState,
  SplashPage,
  SubscriptionContext,
} from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AddOnItem,
  AddOnItemName,
  SearchParams,
  StripeSubscription,
  SubscriptionType,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import {
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  INSTAGRAM_ONBOARDING_CHOICE,
} from "utils/localStorage";
import { getSource, identifyUser, trackEvent } from "utils/tracking";

import ConfirmPaymentForm from "./components/ConfirmPaymentForm";
import InvoicePreview from "./components/InvoicePreview";
import PaymentForm from "./components/PaymentForm";
import PlanDetails from "./components/PlanDetails";
import SkeletonInvoiceForm from "./components/SkeletonInvoiceForm";
import SkeletonPaymentForm from "./components/SkeletonPaymentForm";
import {
  LeftSidebarWrapper,
  MainWrapper,
  RightSidebarWrapper,
} from "./components/Wrapper";
import { getDetails } from "./helpers";
import { SplashPageSourceBackRoute, StripeCustomer } from "./schema";
import { useCheckout } from "./useCheckout";

interface Props {
  openCheckoutDialog?: CheckoutState;
  onSuccess?: () => void;
  onBack?: () => void;
}

const StripeCheckout = ({ openCheckoutDialog, onSuccess, onBack }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg, setCurrentOrg, profile } = useContext(
    OrganizationUserContext,
  );
  const {
    subscription,
    setSubscription,
    setOpenCheckoutDialog,
    setUpgradeDialogSource,
    plans,
    addOns,
    setPlan: setSubscriptionPlan,
  } = useContext(SubscriptionContext);
  const {
    plan,
    customer,
    setCustomer,
    promoCode,
    noNeedsToPay,
    options,
    setOptions,
    addOnItems,
    setRequiresBillingAddress,
    invoice,
    setPageSource,
    setSource,
    setCoupon,
    setPromoCode,
    setPlan,
    setAddOnItems,
  } = useContext(CheckoutContext);

  const { state } = useLocation();

  const getState = () => {
    if (openCheckoutDialog?.plan) {
      return openCheckoutDialog;
    } else {
      return { ...state } as CheckoutState;
    }
  };

  const {
    plan: planFromState,
    pageSource,
    source,
    domainInfo,
    splashPageSource,
    conciergeOption,
  } = getState();

  const isInstagramOnboardingFlow = splashPageSource === SplashPage.instagram;
  const isOOOFlow = splashPageSource === SplashPage.ooo;
  const isProBundleFlow = splashPageSource === SplashPage.pro_bundle;

  const isDialogOpen = Boolean(openCheckoutDialog?.plan);

  const [loading, setLoading] = useState(false);
  const [submitAddressLoading, setSubmitAddressLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { updatePreviewInvoice } = useCheckout();

  const isVaPlan = plan === Plans.VA || plan === Plans.VA_YEARLY;
  const isAddOnItem =
    (addOns && plan && plan in addOns) ||
    splashPageSource === SplashPage.va_bundled;

  const isSwitchingSubscription =
    subscription?.subscription?.status === SubscriptionType.ACTIVE &&
    !isAddOnItem;
  const details = getDetails(plan, plans, addOns, splashPageSource);
  const basedUrl = `${window.location.protocol}//${window.location.host}/`;

  const getReturnUrl = () => {
    if (plan === Plans.CONCIERGE || plan === Plans.CONCIERGE_YEARLY) {
      return `${basedUrl}${routes.launchpadIntakeForm}${location.search}`;
    } else if (splashPageSource === SplashPage.va_bundled) {
      return `${basedUrl}${routes.vaBundledIntakeForm}?${SearchParams.INVOICE_ID}=${invoice?.id}`;
    } else if (isVaPlan) {
      return `${basedUrl}${routes.onboardingVaInfluencer}?checkout=success`;
    } else if (
      isProBundleFlow ||
      (splashPageSource === SplashPage.pro && !profile?.hasOnboarded)
    ) {
      return `${basedUrl}${
        routes.welcomeOnboarding
      }?checkout=success&source=${getSource()}`;
    } else if (
      plan === AddOnItemName.email_volume_monthly ||
      plan === AddOnItemName.email_volume_yearly
    ) {
      return `${basedUrl}${routes.settings}${location.search}?checkout=success`;
    } else {
      return `${basedUrl}${pageSource || routes.settings}?checkout=success`;
    }
  };

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      identifyUser(res.organizationUser.id);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const getOrCreateCustomer = async (organizationId: number) => {
    setLoading(true);
    try {
      const { customer } = await fetcherAuth(
        getToken,
        `/api/organization/${organizationId}/payments/customer`,
        "POST",
      );
      if (customer?.address?.country && customer?.address?.postal_code) {
        setRequiresBillingAddress(false);
      }
      setCustomer(customer);
      const {
        pageSource,
        source,
        coupon: emailCoupon,
        domainInfo,
        splashPageSource,
        conciergeOption,
      } = getState();
      let updatedPromoCode = promoCode;
      if (
        !updatedPromoCode &&
        !promoCode.id &&
        customer.affiliate_promo_code &&
        !emailCoupon
      ) {
        setPromoCode(customer.affiliate_promo_code);
        updatedPromoCode = customer.affiliate_promo_code;
      }
      const presetAddOns = getPresetAddOnItems(plan);
      if (emailCoupon) setCoupon(emailCoupon);
      if (source) setSource(source);
      if (pageSource) setPageSource(pageSource);
      updatePreviewInvoice({
        plan,
        organizationId,
        customer,
        coupon: updatedPromoCode,
        addOnItems: presetAddOns,
        splashPageSource,
        removePromoCode: Boolean(emailCoupon),
        domainInfo,
        conciergeOption,
      });
    } catch (error) {
      setErrorAlert(error);
      setRequiresBillingAddress(true);
    } finally {
      setLoading(false);
    }
  };

  const updateCustomer = async (e: CustomEvent, paymentMethodId?: string) => {
    e.preventDefault();
    if (!currentOrg?.id) return;
    setSubmitAddressLoading(true);
    try {
      const { customer: updatedCustomer } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/customer`,
        "PUT",
        {},
        {
          customerId: customer?.id,
          country: customer?.address?.country,
          postalCode: customer?.address?.postal_code,
          line1: customer?.address?.line1,
          line2: customer?.address?.line2,
          state: customer?.address?.state,
          city: customer?.address?.city,
          paymentMethodId,
          source,
        },
      );
      setCustomer(updatedCustomer);
      updatePreviewInvoice({
        plan,
        organizationId: currentOrg?.id,
        customer: updatedCustomer,
      });
      setRequiresBillingAddress(false);
    } catch (error) {
      setErrorAlert(error);
      setRequiresBillingAddress(true);
    } finally {
      setSubmitAddressLoading(false);
    }
  };

  const generateIntentSecret = async () => {
    if (!currentOrg) return;
    try {
      const { clientSecret } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription/intent`,
        "POST",
      );
      setOptions({
        clientSecret,
      });
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const createSubscription = async () => {
    // Only use for Pro, VA, and Concierge plans.
    setConfirmLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription/create`,
        "POST",
        {},
        {
          plan,
          stripeCustomerId: customer?.id,
          source,
          pageSource,
          splashPageSource,
          promoCode: promoCode?.id,
          couponName: promoCode?.name,
          bundledAddOns: addOnItems,
          domainInfo,
          conciergeOption,
        },
      );
      await postSuccessCheckout(res.subscription, res.items);
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const switchSubscription = async () => {
    // Only use for Pro, VA, and Concierge plans.
    try {
      setConfirmLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription/switch`,
        "POST",
        {},
        {
          plan,
          stripeCustomerId: customer?.id,
          source,
          pageSource,
          isInstagramOnboardingFlow,
          amountDue: invoice?.amount_due,
          promoCode: promoCode?.id,
          couponName: promoCode?.name,
          bundledAddOns: addOnItems,
          splashPageSource,
          conciergeOption,
        },
      );
      await postSuccessCheckout(res.subscription, res.items);
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev: StripeCustomer) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const createAddOns = async () => {
    if (confirmLoading) return;
    const data: Map = {
      stripeCustomerId: customer?.id,
      source,
      promoCode: promoCode?.id,
      couponName: promoCode?.name,
      domainInfo,
    };
    if (invoice?.id) {
      data.invoiceId = invoice.id;
    }
    if (addOnItems && addOnItems.length > 0) {
      data.items = addOnItems;
    } else {
      data.items = [
        {
          name: plan,
          quantity: 1,
        },
      ];
    }
    if (!customer?.id) {
      return;
    }
    try {
      setConfirmLoading(true);

      const { addOnItems } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/create-multi`,
        "POST",
        {},
        data,
      );
      const purchasedPitchingTemplates = addOnItems.filter(
        (item: AddOnItem) => item.itemName === AddOnItemName.pitching_templates,
      );
      if (purchasedPitchingTemplates.length > 0) {
        navigate(`/${routes.intakeForms}/${purchasedPitchingTemplates[0].id}`);
        return;
      }
      const returnUrl = getReturnUrl();
      window.open(returnUrl, "_self");
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (isAddOnItem) {
      await createAddOns();
    } else if (isSwitchingSubscription) {
      await switchSubscription();
    } else {
      await createSubscription();
    }
  };

  const trackSubscription = async (abandonedCheckout?: boolean) => {
    if (!plan) return;
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/payments/subscription/track`,
      "POST",
      {},
      {
        plan,
        isAbandoned: abandonedCheckout,
        isInstagramOnboardingFlow,
      },
    );
  };

  const postSuccessCheckout = async (
    subscription: StripeSubscription,
    items: AddOnItem[],
  ) => {
    // This function is called if a Subscription or a recurring AddOnItem is purchased.
    if (subscription?.status !== SubscriptionType.ACTIVE) {
      // Failed payment, redirect to Settings page so they can retry
      window.open(
        `${basedUrl}dashboard/settings?checkout=failed_payment`,
        "_self",
      );
      return;
    }
    if (
      // @ts-ignore
      typeof window.fbq !== "undefined" &&
      // @ts-ignore
      typeof window.fbq === "function"
    ) {
      // @ts-ignore
      window.fbq("track", "Subscribe", {
        value: details?.price?.toFixed(2),
        currency: "USD",
        predicted_ltv: details?.price
          ? (details?.price * 5).toFixed(2)
          : "0.00",
      });
    }

    if (
      // @ts-ignore
      typeof window.ttq !== "undefined" &&
      // @ts-ignore
      typeof window.ttq.track === "function"
    ) {
      // @ts-ignore
      window.ttq.track("Subscribe", {
        contents: [
          {
            content_id: details.product,
            content_type: "product",
            content_name: details.display_name,
          },
        ],
        value: details?.price?.toFixed(2),
        currency: "USD",
      });
    }

    setSubscription((prev) => {
      if (prev) {
        prev.subscription = subscription;
      }
      return prev;
    });
    if (isVaPlan) {
      setCurrentOrg((prev) => {
        if (prev) {
          return {
            ...prev,
            isVaUser: true,
          };
        }
        return prev;
      });
    }

    if (subscription?.planName) {
      window.open(
        `${basedUrl}${routes.checkoutSuccess}?plan=${subscription?.planName}`,
        "_self",
      );
      return;
    }

    const addOnTemplate =
      items && items?.length > 0
        ? items?.find((x) => x.itemName === AddOnItemName.pitching_templates)
        : null;
    if (!isVaPlan && addOnTemplate?.id) {
      navigate(`/${routes.intakeForms}/${addOnTemplate.id}`);
    } else if (isVaPlan || !isDialogOpen) {
      const returnUrl = getReturnUrl();
      window.open(returnUrl, "_self");
    } else {
      await fetchUserInfo();
      setOpenCheckoutDialog(null);
      setUpgradeDialogSource(null);
      searchParams.set("checkout", "success");
      setSearchParams(searchParams);
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const getPresetAddOnItems = (plan: Plans | AddOnItemName | any) => {
    // Return the right add on items based on the SplashPage source.
    if (isOOOFlow && plan !== AddOnItemName.oui_studio) {
      return [
        {
          name: AddOnItemName.oui_studio,
          quantity: 1,
        },
      ];
    }
    if (isProBundleFlow && plan !== AddOnItemName.partnership_data) {
      return [
        {
          name: AddOnItemName.partnership_data,
          quantity: 1,
        },
      ];
    }
    if (isAddOnItem && splashPageSource !== SplashPage.va_bundled) {
      return [
        {
          name: plan as AddOnItemName,
          quantity: 1,
        },
      ];
    }
    return [];
  };

  useEffect(() => {
    setPlan(planFromState as AddOnItemName | Plans);
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFromState]);

  useEffect(() => {
    return () => {
      // Unmount React component, should clear state properly.
      handleBack();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      currentOrg?.id &&
      (plan || splashPageSource === SplashPage.va_bundled)
    ) {
      trackSubscription();
      if (!customer?.id && Object.keys(addOns)?.length > 0) {
        getOrCreateCustomer(currentOrg?.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, plan, splashPageSource, Object.keys(addOns)?.length]);

  // Generate a SetupIntent (ask for their payment method without charging)
  // This will be stored on the Customer object by Stripe so that we can automatically
  // subscribe them.
  const needsSetupIntentSecret =
    !options?.clientSecret && currentOrg?.id && customer?.id;

  useEffect(() => {
    if (needsSetupIntentSecret) generateIntentSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSetupIntentSecret]);

  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) return <></>;

  const stripe = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

  const clearState = () => {
    setPlan(null);
    setSubscriptionPlan(null);
    setAddOnItems([]);
    setCustomer({
      name: currentOrg?.name || "",
      address: {
        country: "us",
        postal_code: "",
      },
    });
  };

  const handleBack = () => {
    clearState();
    trackSubscription(true);
    if (onBack) {
      onBack();
      return;
    }

    localStorage.removeItem(
      `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    );
    localStorage.removeItem(INSTAGRAM_ONBOARDING_CHOICE);
    if (splashPageSource && splashPageSource in SplashPageSourceBackRoute) {
      if (
        splashPageSource === SplashPage.va_bundled &&
        pageSource === `/${routes.virtualAssistant}`
      ) {
        navigate(`/${routes.virtualAssistant}`);
        return;
      }
      if (splashPageSource === SplashPage.va_money_back) {
        navigate(`/${pageSource}`);
        return;
      }
      if (splashPageSource === SplashPage.instagram) {
        navigate(`/${routes.dashboard}`);
        return;
      }

      navigate(
        `${SplashPageSourceBackRoute[splashPageSource]}${location.search}`,
      );
      return;
    }
    if (isDialogOpen) {
      searchParams.set("checkout", "failed");
      setSearchParams(searchParams);
      setOpenCheckoutDialog(null);
    } else if (pageSource && pageSource !== undefined) {
      navigate(`${pageSource}?checkout=failed`);
    } else {
      navigate(`/${routes.settings}?checkout=failed`);
    }
  };

  const renderPaymentSide = () => {
    return (
      <>
        {loading || !invoice ? (
          <SkeletonPaymentForm />
        ) : (
          <>
            {customer?.payment_method && plan ? (
              <>
                <ConfirmPaymentForm
                  onConfirm={handleConfirm}
                  confirmLoading={confirmLoading}
                  customer={customer}
                  noNeedsToPay={noNeedsToPay}
                  plan={plan}
                />
              </>
            ) : (
              <Elements stripe={stripe} options={options}>
                <Grid
                  item
                  xs={12}
                  md={isDialogOpen ? 12 : 8}
                  xl={isDialogOpen ? 12 : 6}
                >
                  <PaymentForm
                    handleConfirm={handleConfirm}
                    updateCustomer={updateCustomer}
                    submitAddressLoading={submitAddressLoading}
                    returnUrl={getReturnUrl()}
                    setUpClientSecret={options?.clientSecret}
                  />
                </Grid>
              </Elements>
            )}
          </>
        )}
        {isMobileScreen && (
          <Grid container justifyContent={"center"} item xs={12} sx={{ mt: 3 }}>
            <Typography component="div" sx={{ fontSize: 10, color: grey[500] }}>
              Powered by{" "}
              <Link href="https://onbento.com?s=checkout">Bento</Link>
            </Typography>
          </Grid>
        )}
      </>
    );
  };

  return (
    <MainWrapper>
      <LeftSidebarWrapper>
        <Grid item xs={12} xl={isDialogOpen ? 12 : 9}>
          <PlanDetails details={details} handleBack={handleBack} />
          {!invoice ? (
            <SkeletonInvoiceForm />
          ) : (
            <>
              <InvoicePreview
                displayName={details?.display_name}
                productId={details?.product || ""}
              />
              {!isMobileScreen && (
                <Box sx={{ mt: 3, fontSize: 10, color: grey[500] }}>
                  Powered by{" "}
                  <Link href="https://onbento.com?s=checkout">Bento</Link>
                </Box>
              )}
            </>
          )}

          {isMobileScreen && renderPaymentSide()}
        </Grid>
      </LeftSidebarWrapper>

      {!isMobileScreen && (
        <RightSidebarWrapper>{renderPaymentSide()}</RightSidebarWrapper>
      )}
    </MainWrapper>
  );
};

export default StripeCheckout;
